import React from 'react'
import { Badge, Button } from 'react-bootstrap'
import {
	ESTADOS_PRINCIPALES,
	calcularEstados,
	calcularEstadoPrincipal,
	requiereEnvio,
	getEstadoMarcable,
	puedeDesmarcarEstado
} from 'config/pedidos'
import { getDisplayDateTime } from 'utils/date'
import './CronologiaEstados.css'

function CronologiaEstados({
	pedido,
	soloLectura = false,
	onEstadoChange = null
}) {
	if (!pedido) return null

	const estados = calcularEstados(pedido)
	const estadoPrincipalActual = calcularEstadoPrincipal(pedido)
	const estadoMarcable = getEstadoMarcable(pedido)

	// ✅ Funciones para cambios inmediatos
	const marcarEstado = async (estadoKey) => {
		if (onEstadoChange) {
			try {
				await onEstadoChange(estadoKey, new Date().toISOString())
			} catch (error) {
				console.error('Error al marcar estado:', error)
			}
		}
	}

	const desmarcarEstado = async (estadoKey) => {
		if (onEstadoChange) {
			try {
				await onEstadoChange(estadoKey, null)
			} catch (error) {
				console.error('Error al desmarcar estado:', error)
			}
		}
	}

	return (
		<div className="cronologia-estados">
			<div className="estados-secuencia">
				{/* Estado inicial - siempre presente y no modificable */}
				<div className="estado-item estado-completado estado-inmutable">
					<div className="estado-badge-container">
						<Badge bg="success" className="estado-badge">
							<i className="fa fa-inbox me-1"></i>
							Recibido
						</Badge>
					</div>
					<div className="estado-info">
						<small className="estado-fecha">
							{getDisplayDateTime(pedido.recibido)}
						</small>
						<div className="estado-descripcion">
							<small className="text-muted">Pedido creado</small>
						</div>
					</div>
				</div>

				{/* Estados principales de la secuencia */}
				{ESTADOS_PRINCIPALES.filter(estado => estado.key !== 'recibido').map((estado) => {
					// Saltar "enCamino" si no requiere envío
					if (estado.key === 'enCamino' && !requiereEnvio(pedido.tipo)) return null

					const estaCompleto = estados[estado.key] !== null
					const puedeMarcarEste = !soloLectura && estadoMarcable === estado.key
					const puedeDesmarcarEste = !soloLectura && puedeDesmarcarEstado(estado.key, estados, pedido.tipo)

					// Determinar clase CSS
					let claseEstado = 'estado-pendiente'
					if (estaCompleto) {
						claseEstado = 'estado-completado'
					} else if (puedeMarcarEste) {
						claseEstado = 'estado-listo-para-marcar'
					}

					return (
						<div key={estado.key} className={`estado-item ${claseEstado}`}>
							<div className="estado-badge-container">
								<Badge
									bg={estaCompleto ? estado.color : (puedeMarcarEste ? 'outline-primary' : 'secondary')}
									text={estaCompleto ? 'white' : 'dark'}
									className="estado-badge"
								>
									<i className={`fa ${estado.icon} me-1`}></i>
									{estado.label}
								</Badge>
							</div>

							<div className="estado-info">
								{estaCompleto && estados[estado.key] && (
									<small className="estado-fecha">
										{getDisplayDateTime(estados[estado.key])}
									</small>
								)}

								{!estaCompleto && (
									<small className={`${puedeMarcarEste ? 'text-primary fw-bold' : 'text-muted'}`}>
										{puedeMarcarEste ? 'Listo para marcar' : 'Pendiente'}
									</small>
								)}

								{estaCompleto && (
									<div className="estado-descripcion">
										<small className="text-success">
											<i className="fa fa-check-circle me-1"></i>
											Completado
										</small>
									</div>
								)}
							</div>

							{/* Botones de acción */}
							{!soloLectura && (puedeMarcarEste || puedeDesmarcarEste) && (
								<div className="estado-acciones">
									{puedeMarcarEste && (
										<Button
											size="sm"
											variant="outline-success"
											onClick={() => marcarEstado(estado.key)}
											className="estado-btn"
											title={`Marcar como ${estado.label.toLowerCase()}`}
										>
											<i className="fa fa-check me-1"></i>
											Marcar
										</Button>
									)}
									{puedeDesmarcarEste && (
										<Button
											size="sm"
											variant="outline-warning"
											onClick={() => desmarcarEstado(estado.key)}
											className="estado-btn ms-1"
											title={`Desmarcar ${estado.label.toLowerCase()}`}
										>
											<i className="fa fa-undo me-1"></i>
											Desmarcar
										</Button>
									)}
								</div>
							)}
						</div>
					)
				})}

				{/* Estado cancelado si existe */}
				{estados.cancelado && (
					<div className="estado-item estado-cancelado">
						<div className="estado-badge-container">
							<Badge bg="danger" className="estado-badge">
								<i className="fa fa-ban me-1"></i>
								Cancelado
							</Badge>
						</div>
						<div className="estado-info">
							<small className="estado-fecha">
								{getDisplayDateTime(estados.cancelado)}
							</small>
							{pedido.motivoCancelado && (
								<div className="estado-descripcion">
									<small className="text-muted">
										<strong>Motivo:</strong> {pedido.motivoCancelado}
									</small>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Estado finalizado si existe */}
				{estados.finalizado && (
					<div className="estado-item estado-completado">
						<div className="estado-badge-container">
							<Badge bg="dark" className="estado-badge">
								<i className="fa fa-flag-checkered me-1"></i>
								Finalizado
							</Badge>
						</div>
						<div className="estado-info">
							{pedido.fechaFinalizado && (
								<small className="estado-fecha">
									{getDisplayDateTime(pedido.fechaFinalizado)}
								</small>
							)}
							<div className="estado-descripcion">
								<small className="text-muted">Pedido cerrado</small>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default CronologiaEstados